import { Spacer, VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import React from "react";
import styled, { useTheme } from "styled-components/native";
import VDBIcon, { IconsType, IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { Pressable } from "react-native";
import ShadowContainer from "@components/atoms/ShadowContainer";
import { View } from "moti";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { FlatList } from "react-native-gesture-handler";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useBulkRequestSlice } from "@redux/slices/bulkRequestSlice";
import currency from "currency.js";
import { useUserCurrency } from "../../../hooks/getCurrency";
import useAllowedProductTypes from "@components/common/hooks/useAllowedProductTypes";
import { useDispatch } from "react-redux";
import { setProductType } from "@redux/slices/appStateSlice";
import { getHeaderTitle } from "@scenes/bulk-request/Utils";
import { isMeleeItem } from "@utils/product/productValidators";
import { useTranslation } from "react-i18next";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

type BulkRequestProps = {
  navigateToBulkScreen?: any;
  currentProductType?: any;
};

const BulkListSummary = ({
  navigateToBulkScreen,
  currentProductType,
}: BulkRequestProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const bulkRequestSlice = useBulkRequestSlice();
  const bulkItemData = bulkRequestSlice.bulkItemData;
  const userCurrency = useUserCurrency();
  const allowedProducts = useAllowedProductTypes();
  const dispatch = useDispatch();
  const { deviceWidth: screenWidth } = useIsFoldable();
  const productArray = bulkItemData
    .filter((item) => {
      return (
        userInfo?.getAllowedStockTypes()?.includes(item.product_type) &&
        allowedProducts?.find((_item) => _item.key === item.product_type)
      );
    })
    .map((item, index) => {
      const productItem = allowedProducts?.find(
        (_item) => _item.key === item.product_type
      );
      const icon = productItem?.sub_type?.find(
        (subType: any) => subType.name === item.product_sub_type
      )?.icon;

      return {
        ...item,
        id: index,
        label: productItem.label,
        icon: icon?.data || productItem.icon.data,
        type: icon?.type || productItem.icon.type,
        productColor: icon?.icon_color || productItem.icon.icon_color,
        total_price_with_currency: currency(item.total_price, {
          symbol: userCurrency,
          precision: userInfo?.roundUpPrices() && !isMeleeItem(item) ? 0 : 2,
        }).format(),
      };
    });

  const ItemCard = ({ item }: any) => (
    <Pressable
      testID={`item-card-${item.product_type}${
        item.product_sub_type ? `-${item.product_sub_type}` : ""
      }`}
      onPress={() => {
        dispatch(setProductType(item.product_type));
        if (navigateToBulkScreen) {
          navigateToBulkScreen(item.product_type, item.product_sub_type);
        } else if (currentProductType) {
          currentProductType(item.product_type);
        }
      }}
    >
      <CustomView>
        <CardParent>
          <ShadowContainer
            softShadow="1"
            paddingHorizontal="5px"
            backgroundColor={theme["core-01"]}
            borderRadius="10px"
            marginLeft="5px"
            marginRight="5px"
            marginBottom="10px"
            marginTop="10px"
            justifyContent="flex-start"
            width={"100%"}
            maxWidth={"100%"}
            flexDirection="row"
            alignItems="center"
          >
            <CardMain>
              <View style={{ marginRight: 10 }}>
                <VDBIcon
                  name={item.icon}
                  uri={item.icon}
                  size={30}
                  color={theme[item.productColor]}
                  variant={
                    item.type === IconsType.url ||
                    item.type === IconsType.img_url
                      ? IconsVariant.CustomUrl
                      : IconsVariant.VDBCustom
                  }
                />
              </View>
              <ColumnView>
                <RowView>
                  <TitleView screenWidth={screenWidth}>
                    <VDBText
                      color={theme["core-05"]}
                      type={typography["H3-M"]}
                      numberOfLines={1}
                    >
                      {getHeaderTitle(
                        item.product_type,
                        item.product_sub_type,
                        t
                      )}{" "}
                    </VDBText>
                    <VDBText color={theme["core-06"]} type={typography["H3-R"]}>
                      ({item.total_count})
                    </VDBText>
                  </TitleView>
                </RowView>
                <Spacer size={SpaceType.ExtraSmall} />
                <RowView>
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {"Total: "}
                  </VDBText>
                  <VDBText
                    color={theme["core-05"]}
                    type={typography["Minor-M"]}
                  >
                    {item.total_price_with_currency}
                  </VDBText>
                </RowView>
              </ColumnView>
            </CardMain>

            <View>
              <VDBIcon
                name={VDBIcons.ARROW_RIGHT}
                size={24}
                color={theme["core-04"]}
                variant={IconsVariant.VDBCustom}
              />
            </View>
          </ShadowContainer>
        </CardParent>
      </CustomView>
    </Pressable>
  );

  return (
    <Container>
      <FlatList
        data={productArray}
        renderItem={({ item }) => <ItemCard item={item} />}
        keyExtractor={(item) => item.id.toString()}
        contentContainerStyle={{
          backgroundColor: theme["core-02"],
        }}
      />
    </Container>
  );
};

const CardMain = styled.View`
  flex-direction: row;
  align-items: center;
  align-content: flex-start;
  flex: 1;
`;
const CardParent = styled.View`
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const CustomView = styled.View`
  justify-content: center;
  align-items: center;
`;

const Container = styled.View<any>`
  background-color: ${({ theme }) => theme["core-02"]};
  padding: 10px;
`;

const RowView = styled.View`
  flex-direction: row;
  align-items: center;
`;

const ColumnView = styled.View`
  flex-direction: column;
`;

const TitleView = styled.View<{ screenWidth: number }>`
  flex-direction: row;
  max-width: ${({ screenWidth, theme }) =>
    theme.isMobile ? screenWidth / 1.8 : 220}px;
`;

export default BulkListSummary;
